import './LiveTestStatic.scss';
import { useEffect, useMemo, useRef, useState } from 'react';

/*
  Benefits filtering test page, converted from the live-test.html mockup
  (kept at the repo root as reference/backup). Routed at /live-test.

  Two changes to the dashboard benefits filtering are being trialled here
  before they are ported to src/components/common/OfferFilters/:

    1. Breathing room between the page banner and the filter block - the
       bar sits 40px below the banner instead of butting up to it.

    2. The category chips/pills are replaced by a single dropdown showing
       the selected category and a chevron, so the bar keeps one fixed
       height however many categories the API returns. The sort control
       uses the same dropdown so the two match.

  The banner, tiles and category list are dummy data, but the dropdowns
  and the grid are wired up so the filtering can be clicked through. All
  CSS is scoped under ".lt-page" with "lt-" prefixed classes, same
  approach as GiveawayListStatic, so it cannot affect the rest of the app.

  Dark placeholder images are pulled from Unsplash, so the banner and the
  tiles need a network connection to render their image area.
*/

// "rating" and "addedAt" only exist so the two sort options do something
// visible; the real listing sorts server-side.
const tiles = [
  {
    name: 'Bandidos',
    category: 'Motorcycle Accessories',
    shortDescription: 'Best offer. 10% off on all items storewide.',
    offerCode: 'MEGA15',
    image: 'https://images.unsplash.com/photo-1558980664-10eb62d17e08?auto=format&fit=crop&w=600&q=75',
    rating: 4.8,
    addedAt: 3,
  },
  {
    name: 'Vanani',
    category: 'Travel',
    shortDescription: 'Rs. 500 off on all bookings made through the app.',
    offerCode: 'VANANIX',
    image: 'https://images.unsplash.com/photo-1517927033932-b3d18e61fb3a?auto=format&fit=crop&w=600&q=75',
    rating: 4.2,
    addedAt: 6,
  },
  {
    name: 'Dirt Xtreme',
    category: 'Events',
    shortDescription: '10% off on all event tickets, this month only.',
    offerCode: 'BANDIXDIRT',
    image: 'https://images.unsplash.com/photo-1558981403-c5f9899a28bc?auto=format&fit=crop&w=600&q=75',
    rating: 4.6,
    addedAt: 5,
  },
  {
    name: 'Big Bash Sale',
    category: 'Motorcycle Accessories',
    shortDescription: 'Upto 80% off on selected riding gear and accessories.',
    offerCode: 'TAURIAN80',
    image: 'https://images.unsplash.com/photo-1591637333184-19aa84b3e01f?auto=format&fit=crop&w=600&q=75',
    rating: 4.9,
    addedAt: 2,
  },
  {
    name: 'Highway Stays',
    category: 'Travel',
    shortDescription: 'Flat 15% off on weekend stays along the coastal route.',
    offerCode: 'RIDEAWAY',
    image: 'https://images.unsplash.com/photo-1517927033932-b3d18e61fb3a?auto=format&fit=crop&w=600&q=75',
    rating: 4.1,
    addedAt: 4,
  },
  {
    name: 'Track Day',
    category: 'Events',
    shortDescription: 'Members ride the circuit at half the usual session fee.',
    offerCode: 'TRACK50',
    image: 'https://images.unsplash.com/photo-1558981403-c5f9899a28bc?auto=format&fit=crop&w=600&q=75',
    rating: 4.4,
    addedAt: 1,
  },
];

const ALL_CATEGORIES = 'All Categories';

const SORTS = [
  { value: 'topRated', label: 'Top Rated' },
  { value: 'latest', label: 'Latest' },
];

const LtSelect = ({ label, icon, options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const labelId = `lt-label-${label.replace(/\s+/g, '-').toLowerCase()}`;

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const selected = options.find((option) => option.value === value);

  return (
    <div className="lt-field-control">
      <span className="lt-label" id={labelId}>{label}</span>

      <div className={`lt-select ${open ? 'lt-open' : ''}`} ref={rootRef}>
        <button
          type="button"
          className="lt-select-btn"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={labelId}
          onClick={() => setOpen((wasOpen) => !wasOpen)}
        >
          <i className={icon} />
          <span className="lt-select-value">{selected?.label}</span>
          <i className="fa-solid fa-chevron-down lt-caret" />
        </button>

        <ul className="lt-select-menu" role="listbox" aria-labelledby={labelId}>
          {options.map((option) => (
            <li
              key={option.value || 'all'}
              className={`lt-option ${option.value === value ? 'lt-selected' : ''}`}
              role="option"
              aria-selected={option.value === value}
              onClick={() => { onChange(option.value); setOpen(false); }}
            >
              <span>{option.label}</span>
              <i className="fa-solid fa-check" />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const LiveTestStatic = () => {
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('topRated');

  const categoryOptions = useMemo(() => ([
    { value: '', label: ALL_CATEGORIES },
    ...[...new Set(tiles.map((tile) => tile.category))]
      .map((name) => ({ value: name, label: name })),
  ]), []);

  const visibleTiles = useMemo(() => (
    tiles
      .filter((tile) => !category || tile.category === category)
      .sort((a, b) => (sort === 'latest' ? a.addedAt - b.addedAt : b.rating - a.rating))
  ), [category, sort]);

  return (
    <div className="lt-page">

      <div className="lt-banner">
        <img src="https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?auto=format&fit=crop&w=1600&q=75" alt="" />
        <div className="lt-banner-overlay">
          <h1>Offers &amp; Discounts</h1>
        </div>
      </div>

      <div className="lt-container">

        <div className="lt-bar">
          <div className="lt-row">

            <div className="lt-field lt-field-category">
              <LtSelect
                label="Category"
                icon="fa-solid fa-layer-group"
                options={categoryOptions}
                value={category}
                onChange={setCategory}
              />
            </div>

            <div className="lt-field lt-field-sort">
              <LtSelect
                label="Sort by"
                icon="fa-solid fa-arrow-down-wide-short"
                options={SORTS}
                value={sort}
                onChange={setSort}
              />
            </div>

          </div>

          <div className="lt-meta">
            <span className="lt-count">
              {visibleTiles.length} {visibleTiles.length === 1 ? 'offer' : 'offers'}
              {category && <span className="lt-count-in"> in {category}</span>}
            </span>

            {category && (
              <button type="button" className="lt-clear" onClick={() => setCategory('')}>
                <i className="fa-solid fa-xmark" /> Clear filter
              </button>
            )}
          </div>
        </div>

        <div className="lt-grid">
          {visibleTiles.map((tile) => (
            <div className="lt-card" key={tile.name}>

              <div className="lt-card-img">
                <span className="lt-category">{tile.category}</span>
                <img src={tile.image} alt={tile.name} />
              </div>

              <div className="lt-card-body">
                <h3>{tile.name}</h3>
                <p className="lt-short-desc">{tile.shortDescription}</p>
                <span className="lt-code"><i className="fa-solid fa-ticket"></i>{tile.offerCode}</span>

                <div className="lt-actions">
                  <a href="#" className="lt-btn-primary">Details</a>
                  <a href="#" target="_blank" rel="noopener noreferrer" className="lt-btn-secondary">Visit Website</a>
                </div>
              </div>

            </div>
          ))}
        </div>

      </div>

      <footer className="lt-footer">© 2026 Taurian. All rights reserved.</footer>
    </div>
  );
};

export default LiveTestStatic;
